// src/services/cart.ts

import axiosInstance from '../api/axios';
import { AddToCart } from '../api-client/models';
import { toast } from 'react-toastify';
import { useAuthStore } from '../store/AuthStore'; // Importamos el store de autenticación
import { cartApi } from '../api';
import {
  addGuestCartItem,
  getGuestCartData,
  removeGuestCartItem,
  type GuestCartStoredItem,
  updateGuestCartItemQuantity,
} from './guestCart';

// Verificar si el usuario está autenticado
const isAuthenticated = (): boolean => {
  const { accessToken } = useAuthStore.getState();
  return !!accessToken; // Devuelve true si hay un token, false si no
};

export interface GuestCartProductData {
  product_name: string;
  price: number;
  color: string;
  size: string;
  image: string;
}

// Función para agregar productos al carrito
export const addToCart = async (cartOperation: AddToCart, guestProductData?: GuestCartProductData) => {
  if (!isAuthenticated()) {
    if (!guestProductData) {
      toast.error('No se pudo agregar el producto al carrito.', { position: "top-right", autoClose: 3000 });
      return;
    }

    const guestItem: GuestCartStoredItem = {
      id: cartOperation.product_item_id,
      product_item_id: cartOperation.product_item_id,
      quantity: cartOperation.quantity,
      product_name: guestProductData.product_name,
      price: guestProductData.price,
      color: guestProductData.color,
      size: guestProductData.size,
      image: guestProductData.image,
    };

    return addGuestCartItem(guestItem);
  }

  try {
    const response = await axiosInstance.post('/api/shopping-car/cart/', cartOperation);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      // toast.error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', { position: "top-right", autoClose: 3000 });
    } else {
      console.error('Error en la respuesta de la API:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.', { position: "top-right", autoClose: 3000 });
      } else {
        toast.error('Error processing the request.', { position: "top-right", autoClose: 3000 });
      }}
    throw error;
  }
};

// Función para obtener los ítems del carrito
export const getCartItems = async () => {
  if (!isAuthenticated()) {
    return getGuestCartData();
  }

  try {
    const response = await cartApi.shoppingCarCartRetrieve()
    return response.data;
  } catch (error) {
    console.error('Error al obtener los ítems del carrito:', error);
    throw error;
  }
};

// Función para actualizar la cantidad de un ítem en el carrito
export const updateCartItemQuantity = async (cartItemId: number, quantity: number) => {
  if (!isAuthenticated()) {
    return updateGuestCartItemQuantity(cartItemId, quantity);
  }
  
  try {
    const response = await axiosInstance.patch(`/api/shopping-car/cart-item/${cartItemId}/`, { quantity });
    return response.data;
  } catch (error: any) {
    console.error('Error al actualizar la cantidad del ítem:', error);
    if (error.response) {
      toast.error('Error al actualizar la cantidad del ítem.', { position: "top-right", autoClose: 3000 });
    }
    throw error;
  }
};

// Función para eliminar un ítem del carrito
export const removeCartItem = async (cartItemId: number) => {
  if (!isAuthenticated()) {
    return removeGuestCartItem(cartItemId);
  }

  try {
    await axiosInstance.delete(`/api/shopping-car/cart-item/${cartItemId}/`);
    toast.success('Ítem eliminado del carrito.', { position: "top-right", autoClose: 3000 });
  } catch (error) {
    console.error('Error al eliminar el ítem del carrito:', error);
    throw error;
  }
};
